import { useMemo, useState, useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import * as actions from '@app/redux/school/school.action';
import _schoolSltor from '@app/redux/school/school.selector';
import { SCHOOL_SELECT } from '@app/constants/global.const';
import { nonAccentVietnamese } from '@app/utils/utils';
import _locationApis from '@app/apis/locations/location';
import { useDeepEffect } from '@app/hooks/useDeepEffect';
import locationUtils from '@app/utils/location.utils';
import { ScSelectBase } from '@app/components/Base';
import './_style.scss';
import { useSchoolContext } from '@app/components/Context/SchoolContext';
import _ from 'lodash';
import _stringUtils from '@app/utils/string.utils';

const EMPTY_OPTIONS = [{ value: '0', label: '-- Chọn trường --' }];

const normalize = (str) =>
    nonAccentVietnamese(str.replace(/\s/g, '')).toLowerCase();

const SchoolListSelect = () => {
    const { listSchool } = useSchoolContext();

    const currentSchool = useSelector(_schoolSltor.currentSchool);
    const [listLocation, setListLocation] = useState([]);

    const dispatch = useDispatch();

    const selectedValue = useMemo(() => {
        if (_.isEmpty(listSchool)) {
            return '';
        }
        if (currentSchool) return currentSchool;
        const value = localStorage.getItem(SCHOOL_SELECT);
        if (value) return value;
        return listSchool[0].o_id;
    }, [currentSchool, listSchool]);

    useEffect(() => {
        if (_stringUtils.isEmpty(currentSchool)) {
            dispatch(actions.changeSchool(selectedValue));
            localStorage.setItem(SCHOOL_SELECT, selectedValue);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedValue]);

    useEffect(
        () => () => {
            dispatch(actions.doReset());
        },
        [dispatch],
    );

    useDeepEffect(() => {
        if (listSchool.length === 0) return;

        const fetchLocations = async () => {
            const locationIdSet = new Set();
            listSchool.forEach(({ l_id }) => {
                const parts = l_id.split('.');
                locationIdSet.add(l_id);
                locationIdSet.add(`${parts[0]}.${parts[1]}`);
                locationIdSet.add(parts[0]);
            });

            const { status, data } = await _locationApis.getByListId([
                ...locationIdSet,
            ]);
            setListLocation(status === 200 ? data.data : []);
        };

        fetchLocations();
    }, [listSchool]);

    const options = useMemo(() => {
        if (listSchool.length === 0 || listLocation.length === 0) {
            return EMPTY_OPTIONS;
        }

        return listSchool.map((elm) => ({
            value: elm.o_id,
            label: elm.o_name,
            locationFullName: locationUtils.getFullNameById(
                elm.l_id,
                listLocation,
            ),
        }));
    }, [listSchool, listLocation]);

    useEffect(() => {
        if (options !== EMPTY_OPTIONS) {
            dispatch(actions.setListSchool(options));
        }
    }, [options, dispatch]);

    const filterOption = useCallback((input, option) => {
        if (!option?.label) return false;
        return normalize(option.label).includes(normalize(input));
    }, []);

    const onChange = useCallback(
        (value) => {
            dispatch(actions.changeSchool(value));
            localStorage.setItem(SCHOOL_SELECT, value);
        },
        [dispatch],
    );

    const optionRender = useCallback(
        (option) => (
            <>
                {option.label}
                {option.data.locationFullName && (
                    <>
                        <br />
                        <small className="text-muted">
                            {option.data.locationFullName}
                        </small>
                    </>
                )}
            </>
        ),
        [],
    );

    return (
        listSchool.length > 0 && (
            <ScSelectBase
                value={selectedValue}
                onChange={onChange}
                filterOption={filterOption}
                showSearch
                options={options}
                optionRender={optionRender}
                classNames={{ popup: { root: 'school-select' } }}
            />
        )
    );
};

export default SchoolListSelect;
